/**
 * Talking to this computer's print agent (SCOPE §7.1, decision 130).
 *
 * The agent is a small program on the computer the label printer is plugged
 * into (`apps/print-agent`). It listens on localhost only, so this module is
 * the one place the POS reaches outside its own API.
 *
 * **Opt-in, per computer.** Only a browser that has been told "this computer
 * has the agent" (Settings → Label printer on this computer) ever calls it.
 * The registers never do: a page asking for localhost makes Chrome ask the
 * person at the till for permission, and a till with no agent should never
 * see that question. Remembered in this browser's storage, because that is
 * exactly the scope — this browser, on this computer — and it is a
 * convenience that falls back to the browser print when it is missing.
 */

export const PRINT_AGENT_URL: string =
  (import.meta.env.VITE_PRINT_AGENT_URL as string | undefined) ?? 'http://localhost:41980';

const ENABLED_KEY = 'shekko.printAgent';

export interface AgentPrinter {
  readonly name: string;
  readonly isDefault: boolean;
}

export interface AgentStatus {
  readonly agent: string;
  readonly version: string;
  readonly labelPrinter: string | null;
  readonly printers: readonly AgentPrinter[];
}

export class PrintAgentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PrintAgentError';
  }
}

export function printAgentEnabled(): boolean {
  try {
    return window.localStorage.getItem(ENABLED_KEY) === 'on';
  } catch {
    return false;
  }
}

export function setPrintAgentEnabled(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(ENABLED_KEY, 'on');
    else window.localStorage.removeItem(ENABLED_KEY);
  } catch {
    // Storage refused (a private window): the agent simply stays off here.
  }
}

async function call<T>(path: string, init: RequestInit = {}, timeoutMs = 4000): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(`${PRINT_AGENT_URL}${path}`, {
      ...init,
      signal: controller.signal,
      headers: init.body === undefined ? undefined : { 'content-type': 'application/json' },
    });
  } catch {
    throw new PrintAgentError(
      'The print agent is not answering on this computer. Check it is installed and running.',
    );
  } finally {
    window.clearTimeout(timer);
  }

  const body = (await response.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!response.ok) {
    throw new PrintAgentError(body?.error ?? `The print agent refused (${response.status}).`);
  }
  if (body === null) throw new PrintAgentError('The print agent answered with nothing readable.');
  return body;
}

/** Who is running, the printers here, and the label printer chosen. */
export function agentStatus(): Promise<AgentStatus> {
  return call<AgentStatus>('/status', {}, 2500);
}

export function chooseLabelPrinter(labelPrinter: string | null): Promise<AgentStatus> {
  return call<AgentStatus>('/config', { method: 'PUT', body: JSON.stringify({ labelPrinter }) });
}

/** A ZPL run to the label printer. Resolves once the spooler has the whole job. */
export function sendLabels(
  zpl: string,
  document: string,
): Promise<{ printer: string; job: number; bytes: number }> {
  return call('/print', { method: 'POST', body: JSON.stringify({ zpl, document }) }, 15_000);
}
